import { Injectable, Inject, EventEmitter } from '@angular/core';
import { HttpClient, HttpRequest, HttpEventType } from '@angular/common/http';
import * as dayjs from 'dayjs';
import { PatternBeadModel } from '../peditor/patternbeadmodel';
import { BeadCanvas } from '../peditor/beadcanvas';
import { PatternBead } from '../models/pattern/pattern-bead';
import { PaletteDetailModel } from '../models/palettes/palette_detail_model';
import {
  PaletteModel,
  MethodEnum,
  DitherEnum,
  PreEffectEnum,
} from '../models/palettes/palette_model';
import { CategoryModel } from '../models/pattern/category-model';
import {
  ITranslation,
  TranslationEnglish,
  TranslationItalian,
  TranslationGerman,
  TranslationFrench,
  TranslationSpanish,
  TranslationDutch,
  TranslationPortuguese,
  TranslationPolish
} from '../models/translations/translations';
import { Size } from '../peditor/size';
import { RotateDirectionEnum } from '../peditor/patternmodelcontroller';
import { SubscriptionTypeEnum, UserModel } from '../models/user/user-model';
import { Guid } from 'guid-typescript';
import { PatternModel } from '../models/pattern/pattern-model';
import { EMPTY_BEAD } from '../helpers/constants';
import { PaletteRegistryService } from './palette-registry.service';

// Define a lightweight SocialUser interface since we're removing angularx-social-login
export interface SocialUser {
  id: string;
  email: string;
  name: string;
  photoUrl?: string;
  firstName?: string;
  lastName?: string;
  provider: string;
  idToken?: string;
  authToken?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  VERSION: string = '1.0.1';
  userId: string;
  imageFilename: string;
  bcanvas: BeadCanvas;
  lastActionId = 0;
  progress: number;
  user: UserModel;
  socialUser: SocialUser;
  loggedIn: boolean;
  translation: ITranslation = null;
  imageUrl: string = '';
  palettes: PaletteModel[];
  onPalettesRefreshed: EventEmitter<PaletteModel[]> = new EventEmitter<
    PaletteModel[]
  >();
  onTranslationChanged: EventEmitter<ITranslation> = new EventEmitter<
    ITranslation
  >();

  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string,
    private paletteRegistry: PaletteRegistryService,
  ) {
    this.translation = new TranslationEnglish();
  }

  /**
   * Get the palette registry service for palette ID lookups
   */
  getPaletteRegistry(): PaletteRegistryService {
    return this.paletteRegistry;
  }

  getAllPatternsCategory(): CategoryModel {
    return <CategoryModel>{
      categoryId: 0,
      userId: null,
      name: this.getTransation().All,
      description: this.getTransation().AllPatterns,
      createdOn: null,
      lastUpdateOn: null,
    };
  }

  getTransation(): ITranslation {
    return this.translation;
  }

  getTranslationForLanguage(language: LanguageEnum): ITranslation {
    switch (language) {
      case LanguageEnum.Italian:
        return new TranslationItalian();
      case LanguageEnum.German:
        return new TranslationGerman();
      case LanguageEnum.French:
        return new TranslationFrench();
      case LanguageEnum.Spanish:
        return new TranslationSpanish();
      case LanguageEnum.Dutch:
        return new TranslationDutch();
      case LanguageEnum.Portuguese:
        return new TranslationPortuguese();
      case LanguageEnum.Polish:
        return new TranslationPolish();
      case LanguageEnum.English:
      default:
        return new TranslationEnglish();
    }
  }

  setBCanvas(bcanvas: BeadCanvas) {
    this.bcanvas = bcanvas;
    this.updateLastActionId();
  }

  dirtyLastActionId() {
    this.lastActionId = -1;
  }

  updateLastActionId() {
    if (this.bcanvas) {
      this.lastActionId = this.bcanvas.getLastId();
      // console.log("lastActionId => " + this.lastActionId);
    }
  }

  async login() {
    console.log('[HttpService] login() called', {
      userId: this.userId,
      loggedIn: this.loggedIn,
      detectedLanguage: navigator.language,
    });

    try {
      this.user = await this.http
        .post<UserModel>(this.baseUrl + 'user/login', {
          userId: this.userId,
          detectedLanguage: navigator.language,
        })
        .toPromise();

      console.log('[HttpService] login() successful', {
        userId: this.user?.userId,
        language: this.user?.language,
        unitOfMeasure: this.user?.unitOfMeasure,
      });

      this.translation = this.getTranslationForLanguage(this.user.language);
      console.log('[HttpService] Translation updated to:', this.user.language);
      
      // Emit translation changed event so components can update
      this.onTranslationChanged.emit(this.translation);
      
      this.refreshPalettes();
      
      console.log('[HttpService] login() completed - palettes refreshed');
    } catch (error) {
      console.error('[HttpService] login() failed:', error);
      throw error;
    }
  }

  async updateUserProfile() {
    try {
      this.user = await this.http
        .post<UserModel>(this.baseUrl + 'user/updateuserprofile', {
          userId: this.userId,
          unitOfMeasure: this.user.unitOfMeasure,
          language: this.user.language,
          transferSettings: this.user.transferSettings,
          readerSettings: this.user.readerSettings,
        })
        .toPromise();

      // Reload translation when language changes
      this.translation = this.getTranslationForLanguage(this.user.language);
    } catch (error) {
      // Log the error but don't throw - profile update failures shouldn't break the app
      console.error('[HttpService] updateUserProfile failed:', error);

      // Still update the translation locally even if server update failed
      this.translation = this.getTranslationForLanguage(this.user.language);

      // Throw a more user-friendly error
      throw new Error('Failed to save profile settings. Your changes may not be saved on the server.');
    }
  }

  /*
   * General utils for managing cookies in Typescript.
   */
  // setCookie(name: string, val: string) {
  //   const date = new Date();
  //   const value = val;

  //   // Set it expire in 10 years
  //   date.setTime(date.getTime() + (10 * 365 * 24 * 60 * 60 * 1000));

  //   // Set it
  //   document.cookie = name + "=" + value + "; expires=" + date.toUTCString() + "; path=/";
  // }

  // getCookie(name: string) {
  //   const value = "; " + document.cookie;
  //   const parts = value.split("; " + name + "=");

  //   if (parts.length == 2) {
  //     return parts.pop().split(";").shift();
  //   }
  // }

  deleteCookie(name: string) {
    const date = new Date();

    // Set it expire in -1 days
    date.setTime(date.getTime() + -1 * 24 * 60 * 60 * 1000);

    // Set it
    document.cookie = name + '=; expires=' + date.toUTCString() + '; path=/';
  }

  async rotateImage(rotateDirection: RotateDirectionEnum) {
    await this.http
      .post<boolean>(this.baseUrl + 'pattern/rotateimage', {
        rotateDirection: rotateDirection,
        imageFilename: this.user.imageFilename,
      })
      .toPromise();

    var pi = this.bcanvas.getModel().getPatternImage();

    if (pi) {
      this.bcanvas.getModel().getPatternImage().size = new Size(
        pi.size.height,
        pi.size.width,
      );
      this.refreshImage();
    }
  }

  async getPattern(patternId: number): Promise<PatternModel> {
    let patternModel = await this.http
      .post<PatternModel>(this.baseUrl + 'pattern/getpattern', {
        PatternId: patternId,
        UserId: this.userId,
      })
      .toPromise();

    return patternModel;
  }

  async deletePattern(pattern: PatternModel) {
    await this.http
      .post<boolean>(this.baseUrl + 'pattern/delete', pattern)
      .toPromise();
  }

  async addCategory(category: CategoryModel): Promise<CategoryModel> {
    category.userId = this.userId;
    return await this.http
      .post<CategoryModel>(this.baseUrl + 'pattern/addcategory', category)
      .toPromise();
  }

  async editCategory(category: CategoryModel): Promise<CategoryModel> {
    return await this.http
      .post<CategoryModel>(this.baseUrl + 'pattern/editcategory', category)
      .toPromise();
  }

  async deleteCategory(category: CategoryModel): Promise<void> {
    await this.http
      .post<boolean>(this.baseUrl + 'pattern/deletecategory', category)
      .toPromise();
  }

  async getPatterns(
    categoryId: number,
    searchFilter: string,
  ): Promise<PatternModel[]> {
    return await this.http
      .post<PatternModel[]>(this.baseUrl + 'pattern/getpatterns', {
        UserId: this.userId,
        CategoryId: categoryId,
        SearchFilter: searchFilter,
      })
      .toPromise();
  }

  async updatePattern(pattern: PatternModel) {
    return await this.http
      .post<PatternModel>(this.baseUrl + 'pattern/updatepattern', {
        UserId: this.userId,
        PatternId: pattern.patternId,
        Name: pattern.name?.trim() || '',
        Description: pattern.description?.trim() || '',
        CategoryId: pattern.categoryId,
      })
      .toPromise();
  }

  async savePattern(
    pattern: PatternModel,
    patternBead: PatternBead,
    isTemporary: boolean,
    onBusyRetry?: (attempt: number, maxAttempts: number) => void,
  ): Promise<PatternModel> {
    // Guard against null userId (race condition with logout)
    if (!this.userId) {
      console.warn('[HttpService] savePattern aborted: userId is null');
      throw new Error('User not authenticated');
    }

    const patternData = this.serializePatternBead(patternBead);
    const patternSize = new Blob([patternData]).size; // Size in bytes

    try {
      const body = {
        PatternId: pattern.patternId,
        Name: pattern.name?.trim() || '',
        Description: pattern.description?.trim() || '',
        CategoryId: pattern.categoryId,
        UserId: this.userId,
        PatternData: patternData,
        IsTemporary: isTemporary,
        ImageFilename: this.user?.imageFilename || null,
      };

      // The server answers 503 (Retry-After) when it could not take the write
      // lock; nothing was written, so retrying is safe.
      const maxBusyRetries = 3;
      let savedPattern: PatternModel;
      for (let attempt = 0; ; attempt++) {
        try {
          savedPattern = await this.http
            .post<PatternModel>(this.baseUrl + 'pattern/savepattern', body)
            .toPromise();
          break;
        } catch (e) {
          if (e?.status !== 503 || attempt >= maxBusyRetries) throw e;
          const backoffMs = 2000 * Math.pow(2, attempt); // 2s, 4s, 8s
          const retryAfterMs =
            (parseInt(e.headers?.get?.('Retry-After'), 10) || 0) * 1000;
          if (onBusyRetry) onBusyRetry(attempt + 1, maxBusyRetries);
          await new Promise(resolve =>
            setTimeout(resolve, Math.max(backoffMs, retryAfterMs)),
          );
        }
      }

      if (!isTemporary) this.updateLastActionId();

      return savedPattern;
    } catch (error) {
      // Re-throw with enhanced error information for component-level handling
      const enhancedError = {
        originalError: error,
        patternInfo: {
          patternId: pattern.patternId,
          name: pattern.name,
          isTemporary: isTemporary,
          patternSizeBytes: patternSize,
          patternSizeKB: Math.round(patternSize / 1024),
          gridSize: {
            rows: patternBead.grid.length,
            cols: patternBead.grid[0]?.length || 0,
          },
        },
        errorDetails: {
          status: error?.status || 0,
          statusText: error?.statusText || 'Unknown Error',
          message: error?.error?.message || error?.message || 'Pattern save failed',
        },
      };

      throw enhancedError;
    }
  }

  deserializePatternBead(patternData: string): PatternBead {
    let patternBead = new PatternBead();

    let data = JSON.parse(patternData);

    patternBead.stitchId = data.stitchId;
    patternBead.posX = data.posX;
    patternBead.posY = data.posY;
    patternBead.beadWidth = data.beadWidth;
    patternBead.beadHeight = data.beadHeight;
    patternBead.image = data.image;
    patternBead.grid = [];
    patternBead.rotation = data.rotation;

    let grid = data.grid;
    let paletteDetails = <PaletteDetailModel[]>data.paletteDetails;

    for (let nRow = 0; nRow < grid.length; nRow++) {
      let row: PaletteDetailModel[] = [];

      for (let nCol = 0; nCol < grid[0].length; nCol++) {
        let paletteBeadId = grid[nRow][nCol];

        let pd: PaletteDetailModel;

        if (paletteBeadId !== -1) {
          pd = paletteDetails.find((x) => x.paletteBeadId === paletteBeadId) ?? EMPTY_BEAD;
        } else {
          pd = EMPTY_BEAD;
        }

        row.push(pd);
      }

      patternBead.grid.push(row);
    }

    return patternBead;
  }

  serializePatternBead(patternBead: PatternBead): string {
    let paletteDetails: PaletteDetailModel[] = [];
    let grid = [];

    for (let nRow = 0; nRow < patternBead.grid.length; nRow++) {
      const gridRow = patternBead.grid[nRow];

      let row = [];
      for (let nCol = 0; nCol < gridRow.length; nCol++) {
        let pd = patternBead.grid[nRow][nCol];

        if (pd) {
          row.push(pd.paletteBeadId);

          if (
            paletteDetails.findIndex(
              (x) => x.paletteBeadId === pd.paletteBeadId,
            ) === -1
          ) {
            paletteDetails.push(pd);
          }
        }
      }

      grid.push(row);
    }

    let data = {
      stitchId: patternBead.stitchId,
      posX: patternBead.posX,
      posY: patternBead.posY,
      beadWidth: patternBead.beadWidth,
      beadHeight: patternBead.beadHeight,
      paletteDetails: paletteDetails,
      image: patternBead.image,
      grid: grid,
      rotation: patternBead.rotation,
    };

    // Estimate pattern size before JSON.stringify to prevent errors
    const rows = grid.length;
    const cols = grid[0]?.length || 0;
    const estimatedSize = rows * cols * 20; // ~20 bytes per bead

    // Check if pattern might be too large (100MB threshold)
    const MAX_SIZE_BYTES = 100 * 1024 * 1024; // 100MB
    if (estimatedSize > MAX_SIZE_BYTES) {
      throw new Error(
        `Pattern is too large to save (${rows}×${cols} = ${rows * cols} beads). ` +
        `Maximum supported size is approximately 5000×5000 beads. ` +
        `Consider splitting into smaller patterns or reducing the grid size.`
      );
    }

    try {
      return JSON.stringify(data);
    } catch (error) {
      if (error.message && error.message.includes('Invalid string length')) {
        // JSON.stringify failed due to size - provide helpful error
        throw new Error(
          `Pattern is too large to serialize (${rows}×${cols} = ${rows * cols} beads). ` +
          `This pattern exceeds the browser's maximum string length. ` +
          `Please reduce the pattern size or split it into multiple smaller patterns.`
        );
      }
      // Re-throw other errors
      throw error;
    }
  }

  async getCategories(): Promise<CategoryModel[]> {
    return await this.http
      .post<CategoryModel[]>(this.baseUrl + 'pattern/getcategories', {
        userId: this.userId,
      })
      .toPromise();
  }

  async refreshPalettes(): Promise<void> {
    this.palettes = await this.getPalettes(true);
    // Initialize the palette registry with loaded palettes
    this.paletteRegistry.initialize(this.palettes);
    this.onPalettesRefreshed.emit(this.palettes);
  }

  async getPalettes(includeBuiltIn: boolean): Promise<PaletteModel[]> {
    return await this.http
      .post<PaletteModel[]>(this.baseUrl + 'palette/getpalettes', {
        includeBuiltIn,
        userId: this.userId,
      })
      .toPromise();
  }

  async getPaletteDetails(
    paletteId: number,
    filter: string,
    pageSize: number,
    paletteIdToExclude: number,
  ): Promise<PaletteDetailModel[]> {
    return await this.http
      .post<PaletteDetailModel[]>(this.baseUrl + 'palette/details', <
        GetPaletteDetailsRequest
      >{
        paletteId: paletteId,
        filter: filter,
        pageSize: pageSize + 1,
        paletteIdToExclude: paletteIdToExclude,
      })
      .toPromise();
  }

  canSavePattern(): boolean {
    if (this.bcanvas) return this.lastActionId != this.bcanvas.getLastId();
    else return false;
  }

  async uploadImage(files: FileList | File[]) {
    if (files.length === 0) return;

    const formData = new FormData();

    this.imageFilename = Guid.create().toString().split('-').join('');

    formData.append('userId', this.userId);
    formData.append('imageFilename', this.imageFilename);

    for (let file of Array.from(files)) {
      // Validate file size (15MB hard limit)
      const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('FILE_TOO_LARGE');
      }

      // Compress image before uploading with memory error detection
      try {
        const compressedFile = await this.compressImage(file);
        formData.append(file.name, compressedFile);
      } catch (error) {
        // Detect memory-related errors
        if (error instanceof RangeError ||
            (error?.message && (error.message.includes('memory') || error.message.includes('allocation')))) {
          throw new Error('MEMORY_ERROR');
        }
        throw error;
      }
    }

    const uploadReq = new HttpRequest(
      'POST',
      `pattern/uploadfile`,
      formData,
      {},
    );

    try {
      let event = await this.http.request(uploadReq).toPromise();

      if (event.type === HttpEventType.Response) {
        if (event.status == 200) {
          let result = <UploadImageResult>event.body;
          if (this.user) {
            this.user.imageFilename = result.imageFilename;
          }
          this.imageUrl = result.url;
          this.refreshImage();
        }
      }
    } catch (error) {
      // Extract error message from the backend response
      let errorMessage = 'Upload failed';
      if (error?.error && typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      throw new Error(errorMessage);
    }
  }

  private isLowMemoryDevice(): boolean {
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const heapLimit = (performance as any).memory?.jsHeapSizeLimit || Infinity;
    // Devices with <300MB heap limit are considered low-memory
    return isMobile && heapLimit < 300 * 1024 * 1024;
  }

  private async compressImage(file: File): Promise<File> {
    const startTime = Date.now();
    const inputSizeKB = Math.round(file.size / 1024);

    // If file is not an image, return as-is
    if (!file.type.startsWith('image/')) {
      return file;
    }

    return new Promise((resolve, reject) => {
      // Use Object URL instead of Base64 to save memory (eliminates 8-26MB string allocation)
      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        // Clean up object URL immediately after image loads
        URL.revokeObjectURL(url);

        // Adaptive target resolution based on device capabilities
        let maxSize = 1000; // Default: 1000px gives 2x resolution per bead (pattern grid max is 500x500)
        if (this.isLowMemoryDevice() && file.size > 10 * 1024 * 1024) {
          maxSize = 800; // Reduce target for low-memory devices with large files
        }

        const originalWidth = img.width;
        const originalHeight = img.height;
        let width = originalWidth;
        let height = originalHeight;

        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        // Determine output format and quality
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const quality = file.type === 'image/png' ? 0.95 : 0.90;

        // Use progressive downscaling for low-memory devices with large images
        // This reduces peak memory by 50-75% by resizing in multiple steps
        if (this.isLowMemoryDevice() && (originalWidth > maxSize * 2 || originalHeight > maxSize * 2)) {
          this.progressiveDownscale(img, width, height, outputType, quality, file.name)
            .then((compressedFile) => {
              const duration = Date.now() - startTime;
              const outputSizeKB = Math.round(compressedFile.size / 1024);
              console.log(`Image compressed (progressive): ${inputSizeKB}KB → ${outputSizeKB}KB (${width}x${height}) in ${duration}ms`);

              // Clean up image reference
              img.src = '';

              resolve(compressedFile);
            })
            .catch(reject);
        } else {
          // Direct resize for desktop/high-memory devices (fast path)
          this.directResize(img, width, height, outputType, quality, file.name)
            .then((compressedFile) => {
              const duration = Date.now() - startTime;
              const outputSizeKB = Math.round(compressedFile.size / 1024);
              console.log(`Image compressed (direct): ${inputSizeKB}KB → ${outputSizeKB}KB (${width}x${height}) in ${duration}ms`);

              // Clean up image reference
              img.src = '';

              resolve(compressedFile);
            })
            .catch(reject);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  private async directResize(
    img: HTMLImageElement,
    targetWidth: number,
    targetHeight: number,
    outputType: string,
    quality: number,
    fileName: string
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Convert to blob with compression
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], fileName, {
              type: outputType,
              lastModified: Date.now(),
            });

            // Explicit memory cleanup
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;

            resolve(compressedFile);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        outputType,
        quality
      );
    });
  }

  private async progressiveDownscale(
    img: HTMLImageElement,
    targetWidth: number,
    targetHeight: number,
    outputType: string,
    quality: number,
    fileName: string
  ): Promise<File> {
    let currentWidth = img.width;
    let currentHeight = img.height;
    let currentImage = img;

    // Downscale in steps of 50% until we reach target size
    // This reduces peak memory usage significantly
    while (currentWidth > targetWidth * 1.5 || currentHeight > targetHeight * 1.5) {
      const stepWidth = Math.max(Math.floor(currentWidth / 2), targetWidth);
      const stepHeight = Math.max(Math.floor(currentHeight / 2), targetHeight);

      const canvas = document.createElement('canvas');
      canvas.width = stepWidth;
      canvas.height = stepHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(currentImage, 0, 0, stepWidth, stepHeight);

      // Convert canvas to image for next iteration
      const imageData = ctx.getImageData(0, 0, stepWidth, stepHeight);

      // Create new image from canvas for next step
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = stepWidth;
      tempCanvas.height = stepHeight;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.putImageData(imageData, 0, 0);

      // Clean up previous canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 0;
      canvas.height = 0;

      // Update dimensions for next iteration
      currentWidth = stepWidth;
      currentHeight = stepHeight;
      currentImage = tempCanvas as any; // Treat canvas as image source
    }

    // Final resize to exact target dimensions
    return this.directResize(currentImage as any, targetWidth, targetHeight, outputType, quality, fileName);
  }

  refreshImage() {
    let patternImage = new Image();
    patternImage.src = this.imageUrl + '?v=' + new Date().getTime();
    patternImage.onload = () => {
      this.bcanvas.patternImageLoaded = true;
      this.bcanvas.insertImage(patternImage);
      this.bcanvas.repaint();
    };
  }

  async transferImage(
    paletteId: number,
    nColors: number,
    preEffect: PreEffectEnum,
    method: MethodEnum,
    dither: DitherEnum,
    model: PatternBeadModel,
  ) {
    let patternData = await this.http
      .post<string>(this.baseUrl + 'pattern/transferimage', {
        stitchId: model.getStitchId(),
        paletteId: paletteId,
        userId: this.userId,
        imageFilename: this.user.imageFilename,
        nColors: nColors,
        preEffect: preEffect,
        method: method,
        dither: dither,
        beadWidth: model.getBeadSize().width,
        beadHeight: model.getBeadSize().height,
        cols: model.getCols(),
        rows: model.getRows(),
        patternXOffs: Math.round(model.getPosition().x),
        patternYOffs: Math.round(model.getPosition().y),
        imgXOffs: Math.round(model.getPatternImage().pos.x),
        imgYOffs: Math.round(model.getPatternImage().pos.y),
        imgWidth: Math.round(model.getPatternImage().size.width),
        imgHeight: Math.round(model.getPatternImage().size.height),
        rot: model.getPatternImage().rot,
      })
      .toPromise();

    let patternBead = this.deserializePatternBead(JSON.stringify(patternData));
    this.bcanvas.controller.transferImage(patternBead.grid);
    this.bcanvas.repaint();
  }

  async addPalette(palette: PaletteModel): Promise<PaletteModel> {
    const newPaletter = await this.http
      .post<PaletteModel>(this.baseUrl + 'palette/add', palette)
      .toPromise();

    this.palettes.push(newPaletter);
    this.onPalettesRefreshed.emit(this.palettes);
    return newPaletter;
  }

  async updatePalette(palette: PaletteModel) {
    return await this.http
      .post<boolean>(this.baseUrl + 'palette/update', palette)
      .toPromise();
  }

  async removePalette(palette: PaletteModel) {
    await this.http
      .post<boolean>(this.baseUrl + 'palette/remove', palette)
      .toPromise();

    this.palettes.splice(
      this.palettes.findIndex((p) => p.paletteId == palette.paletteId),
      1,
    );
    this.onPalettesRefreshed.emit(this.palettes);
  }

  async addPaletteDetail(paletteId: number, paletteBeadId: number) {
    await this.http
      .post<boolean>(this.baseUrl + 'palette/details/add', {
        paletteId: paletteId,
        paletteBeadId: paletteBeadId,
      })
      .toPromise();
  }

  async removePaletteDetail(paletteDetailId: number) {
    await this.http
      .post<boolean>(this.baseUrl + 'palette/details/remove', {
        paletteDetailId: paletteDetailId,
      })
      .toPromise();
  }

  async createCheckoutSession(subscriptionType: SubscriptionTypeEnum) {
    return await this.http
      .post<any>(this.baseUrl + 'payments/create-checkout-session', {
        userId: this.userId,
        baseUrl: this.baseUrl,
        subscriptionType: subscriptionType,
      })
      .toPromise();
  }

  async createCustomerPortalSession() {
    return await this.http
      .post<any>(this.baseUrl + 'payments/create-customer-portal-session', {
        customerId: this.user.stripeCustomerId,
        baseUrl: this.baseUrl,
      })
      .toPromise();
  }

  isSubscriptionActive(): boolean {
    if (!this.user) {
      return false;
    }

    // Check paid subscription
    if (this.user.subscriptionType === SubscriptionTypeEnum.None) {
      return false;
    }

    const date = this.user.subscriptionLastDateTime;
    if (!date) {
      return false;
    }

    let expireDate = undefined;

    if (this.user.subscriptionType === SubscriptionTypeEnum.Monthly) {
      expireDate = dayjs(date).add(1, 'month').endOf('day');
    } else if (this.user.subscriptionType === SubscriptionTypeEnum.Yearly) {
      expireDate = dayjs(date).add(1, 'year').endOf('day');
    }

    return expireDate && expireDate > dayjs();
  }
}

interface UploadImageResult {
  message: string;
  imageFilename: string;
  url: string;
}

export interface GetPaletteDetailsRequest {
  paletteId: number;
  filter: string;
  pageSize: number;
  paletteIdToExclude: number;
}

export enum UnitOfMeasureEnum {
  Inch = 1,
  Cm = 2,
  Mm = 3,
}

export enum LanguageEnum {
  English = 1,
  Italian = 2,
  German = 3,
  French = 4,
  Spanish = 5,
  Dutch = 6,
  Portuguese = 7,
  Polish = 8,
}
